import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Bell, MessageSquare, Inbox, X, BellRing, CheckCheck, CalendarClock, FileText } from 'lucide-react';
import { Toaster, toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { BACKEND_URL } from '../../lib/backend';

const POLL_MS = 20000;
export const NOTIF_REFRESH_EVENT = 'vf-admin-notifications-refresh';
export const NOTIF_DATA_EVENT = 'vf-admin-notifications';

const timeAgo = (iso) => {
  if (!iso) return '';
  const m = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (m < 1) return 'adesso';
  if (m < 60) return `${m} min fa`;
  if (m < 1440) return `${Math.round(m / 60)} h fa`;
  return new Date(iso).toLocaleDateString('it-IT', { day: '2-digit', month: 'short' });
};

const desktopNotify = (title, body) => {
  if (typeof Notification === 'undefined' || Notification.permission !== 'granted' || !document.hidden) return;
  try { new Notification(title, { body, icon: '/favicon.ico' }); } catch { /* unsupported */ }
};

const useAdminNotifications = (token, enabled) => {
  const [data, setData] = useState(null);
  const prev = useRef(null);
  const navigate = useNavigate();

  const fetchNow = useCallback(async () => {
    if (!token || !enabled) return;
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/notifications`, { headers: { Authorization: `Bearer ${token}` } });
      const d = res.data;
      const p = prev.current;
      if (p) {
        d.conversations.forEach((c) => {
          const before = p.conversations.find((x) => x.user_id === c.user_id);
          if (!before || c.count > before.count) {
            toast(`Nuovo messaggio da ${c.name}`, {
              description: c.preview,
              action: { label: 'Apri', onClick: () => navigate(`/admin?tab=messaging&user=${c.user_id}`) },
            });
            desktopNotify(`Nuovo messaggio da ${c.name}`, c.preview);
          }
        });
        const known = new Set(p.new_leads.map((l) => l.id));
        d.new_leads.filter((l) => !known.has(l.id)).forEach((l) => {
          toast(`Nuovo lead: ${l.name || l.email}`, {
            description: l.source || 'Modulo sito',
            action: { label: 'Vedi', onClick: () => navigate('/admin?tab=leads') },
          });
          desktopNotify(`Nuovo lead: ${l.name || l.email}`, l.source || 'Modulo sito');
        });
        const knownEv = new Set((p.offer_events || []).map((e) => e.id));
        (d.offer_events || []).filter((e) => !knownEv.has(e.id)).forEach((e) => {
          toast(e.title, { action: { label: 'Apri', onClick: () => navigate(`/admin?tab=leads&lead=${e.lead_id}`) } });
          desktopNotify('Offerta', e.title);
        });
      }
      prev.current = d;
      setData(d);
      window.dispatchEvent(new CustomEvent(NOTIF_DATA_EVENT, { detail: d }));
    } catch { /* silent: next poll retries */ }
  }, [token, enabled, navigate]);

  useEffect(() => {
    if (!enabled) return undefined;
    fetchNow();
    const id = setInterval(fetchNow, POLL_MS);
    window.addEventListener(NOTIF_REFRESH_EVENT, fetchNow);
    return () => { clearInterval(id); window.removeEventListener(NOTIF_REFRESH_EVENT, fetchNow); };
  }, [enabled, fetchNow]);

  return { data, fetchNow };
};

const useTitleBadge = (total) => {
  useEffect(() => {
    const base = document.title.replace(/^\(\d+\)\s/, '');
    document.title = total > 0 ? `(${total}) ${base}` : base;
  }, [total]);
};

const RoleTag = ({ role }) => (
  <span className={`text-[10px] px-1.5 py-0.5 rounded font-semibold uppercase tracking-wide ${role === 'lead' ? 'bg-yellow-500/20 text-yellow-300' : 'bg-blue-500/20 text-blue-300'}`}>
    {role === 'lead' ? 'Lead' : 'Cliente'}
  </span>
);

const ConversationRow = ({ c, onOpen }) => (
  <button onClick={onOpen} className="w-full text-left px-4 py-3 hover:bg-white/5 transition-colors flex gap-3" data-testid={`notif-conv-${c.user_id}`}>
    <span className="mt-0.5 w-8 h-8 rounded-full bg-emerald-500/15 flex items-center justify-center flex-shrink-0">
      <MessageSquare className="w-4 h-4 text-emerald-400" />
    </span>
    <span className="flex-1 min-w-0">
      <span className="flex items-center gap-2">
        <span className="text-white text-sm font-medium truncate">{c.name}</span>
        <RoleTag role={c.role} />
        <span className="ml-auto text-[11px] text-slate-500 flex-shrink-0">{timeAgo(c.created_at)}</span>
      </span>
      <span className="block text-xs text-slate-400 truncate mt-0.5">{c.preview}</span>
    </span>
    <span className="self-center bg-emerald-500 text-white text-[11px] font-bold rounded-full min-w-[20px] h-5 px-1.5 flex items-center justify-center">{c.count}</span>
  </button>
);

const LeadRow = ({ l, onOpen }) => (
  <button onClick={onOpen} className="w-full text-left px-4 py-3 hover:bg-white/5 transition-colors flex gap-3" data-testid={`notif-lead-${l.id}`}>
    <span className="mt-0.5 w-8 h-8 rounded-full bg-amber-500/15 flex items-center justify-center flex-shrink-0">
      <Inbox className="w-4 h-4 text-amber-400" />
    </span>
    <span className="flex-1 min-w-0">
      <span className="flex items-center gap-2">
        <span className="text-white text-sm font-medium truncate">{l.name || l.email}</span>
        <span className="ml-auto text-[11px] text-slate-500 flex-shrink-0">{timeAgo(l.created_at)}</span>
      </span>
      <span className="block text-xs text-slate-400 truncate mt-0.5">{l.email}{l.source ? ` · ${l.source}` : ''}</span>
    </span>
  </button>
);

const TONES = { violet: 'bg-violet-500/15 text-violet-300', rose: 'bg-rose-500/15 text-rose-300', amber: 'bg-amber-500/15 text-amber-300' };
const SimpleRow = ({ icon: Icon, tone, title, sub, onOpen, testId }) => (
  <button onClick={onOpen} className="w-full text-left px-4 py-3 hover:bg-white/5 transition-colors flex gap-3" data-testid={testId}>
    <span className={`mt-0.5 w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${TONES[tone]}`}><Icon className="w-4 h-4" /></span>
    <span className="flex-1 min-w-0">
      <span className="block text-white text-sm font-medium truncate">{title}</span>
      <span className="block text-xs text-slate-400 truncate mt-0.5">{sub}</span>
    </span>
  </button>
);

const NotificationPanel = ({ data, onClose, onNavigate, onLeadsSeen, onOffersSeen }) => {
  const [perm, setPerm] = useState(typeof Notification !== 'undefined' ? Notification.permission : 'unsupported');
  const askPermission = async () => setPerm(await Notification.requestPermission());
  const empty = data && data.total === 0;
  return (
    <div className="absolute bottom-16 left-0 w-[360px] max-w-[calc(100vw-2rem)] bg-slate-900/95 backdrop-blur-xl border border-white/10 rounded-2xl shadow-2xl overflow-hidden animate-in fade-in slide-in-from-bottom-2 duration-200" data-testid="notif-panel">
      <div className="px-4 py-3 border-b border-white/10 flex items-center justify-between">
        <span className="text-white font-semibold text-sm">Notifiche</span>
        <button onClick={onClose} className="text-slate-400 hover:text-white" data-testid="notif-close-btn"><X className="w-4 h-4" /></button>
      </div>
      <div className="max-h-[60vh] overflow-y-auto">
        {empty && <p className="px-4 py-8 text-center text-sm text-slate-500" data-testid="notif-empty">Tutto letto. Nessuna novità.</p>}
        {data?.conversations?.length > 0 && (
          <div>
            <p className="px-4 pt-3 pb-1 text-[11px] uppercase tracking-widest text-slate-500">Messaggi non letti · {data.unread_messages}</p>
            {data.conversations.map((c) => (
              <ConversationRow key={c.user_id} c={c} onOpen={() => onNavigate(`/admin?tab=messaging&user=${c.user_id}`)} />
            ))}
          </div>
        )}
        {data?.new_leads?.length > 0 && (
          <div className="border-t border-white/5">
            <div className="px-4 pt-3 pb-1 flex items-center justify-between">
              <p className="text-[11px] uppercase tracking-widest text-slate-500">Nuovi lead · {data.new_leads_count}</p>
              <button onClick={onLeadsSeen} className="text-[11px] text-amber-400 hover:text-amber-300 flex items-center gap-1" data-testid="notif-leads-seen-btn">
                <CheckCheck className="w-3 h-3" /> Segna come visti
              </button>
            </div>
            {data.new_leads.map((l) => <LeadRow key={l.id} l={l} onOpen={() => onNavigate(`/admin?tab=leads&lead=${l.id}`)} />)}
          </div>
        )}
        {data?.offer_events?.length > 0 && (
          <div className="border-t border-white/5">
            <div className="px-4 pt-3 pb-1 flex items-center justify-between">
              <p className="text-[11px] uppercase tracking-widest text-slate-500">Offerte</p>
              <button onClick={onOffersSeen} className="text-[11px] text-amber-400 hover:text-amber-300 flex items-center gap-1" data-testid="notif-offers-seen-btn"><CheckCheck className="w-3 h-3" /> Segna come viste</button>
            </div>
            {data.offer_events.map((e) => (
              <SimpleRow key={e.id} icon={FileText} tone="violet" title={e.title} sub={timeAgo(e.at)} testId={`notif-offer-${e.id}`} onOpen={() => onNavigate(`/admin?tab=leads&lead=${e.lead_id}`)} />
            ))}
          </div>
        )}
        {data?.tasks_due?.length > 0 && (
          <div className="border-t border-white/5">
            <p className="px-4 pt-3 pb-1 text-[11px] uppercase tracking-widest text-slate-500">Attività da fare oggi / in ritardo · {data.tasks_due_count}</p>
            {data.tasks_due.map((t) => (
              <SimpleRow key={t.id} icon={CalendarClock} tone={new Date(t.due_at) < new Date() ? 'rose' : 'amber'} title={t.title} sub={`${t.lead_name} · ${new Date(t.due_at).toLocaleString('it-IT', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}`} testId={`notif-task-${t.id}`} onOpen={() => onNavigate(`/admin?tab=leads&lead=${t.lead_id}`)} />
            ))}
          </div>
        )}
      </div>
      {perm === 'default' && (
        <button onClick={askPermission} className="w-full px-4 py-2.5 text-xs text-cyan-300 bg-cyan-500/10 hover:bg-cyan-500/20 flex items-center justify-center gap-2 border-t border-white/10" data-testid="notif-desktop-btn">
          <BellRing className="w-3.5 h-3.5" /> Attiva notifiche desktop (anche a scheda chiusa)
        </button>
      )}
    </div>
  );
};

export const AdminNotificationCenter = () => {
  const { token, user } = useAuth();
  const enabled = !!token && user?.role === 'admin';
  const { data, fetchNow } = useAdminNotifications(token, enabled);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const total = data?.total || 0;
  useTitleBadge(enabled ? total : 0);

  if (!enabled) return null;

  const markSeen = (what) => async () => {
    await axios.post(`${BACKEND_URL}/api/admin/notifications/leads-seen?what=${what}`, {}, { headers: { Authorization: `Bearer ${token}` } }).catch(() => {});
    fetchNow();
  };
  const go = (path) => { setOpen(false); navigate(path); };

  return (
    <>
      <Toaster theme="dark" position="top-right" richColors closeButton duration={8000} />
      <div className="fixed bottom-5 left-5 z-[60]">
        {open && <NotificationPanel data={data} onClose={() => setOpen(false)} onNavigate={go} onLeadsSeen={markSeen('leads')} onOffersSeen={markSeen('offers')} />}
        <button
          onClick={() => setOpen((o) => !o)}
          className={`relative w-12 h-12 rounded-full flex items-center justify-center shadow-xl border transition-[transform,background-color] duration-200 hover:scale-105 ${total > 0 ? 'bg-amber-500 border-amber-300 text-slate-950' : 'bg-slate-800 border-white/10 text-slate-200'}`}
          data-testid="notif-bell-btn"
          aria-label="Notifiche"
        >
          <Bell className={`w-5 h-5 ${total > 0 ? 'animate-[wiggle_1.2s_ease-in-out_infinite]' : ''}`} />
          {total > 0 && (
            <span className="absolute -top-1 -right-1 min-w-[22px] h-[22px] px-1 rounded-full bg-red-600 text-white text-[11px] font-bold flex items-center justify-center ring-2 ring-slate-950" data-testid="notif-bell-count">
              {total > 99 ? '99+' : total}
            </span>
          )}
        </button>
      </div>
    </>
  );
};
